<template lang="pug">
VSheet.px-4.px-md-6.px-lg-12.pt-4.pb-2.rounded-t(
  :color="theme.current.value.colors.background"
  data-testid="BibleVerseCommentListSheet"
)
  div.d-flex.align-center.justify-space-between.mb-4
    HH.text-h4 {{ title }}
    VBtn(
      size="small"
      variant="plain"
      :icon="true"
      @click="openSheet = false"
    )
      VIcon {{ mdiClose }}
  div.mx-n4.overflow-x-auto
    div.drag-scroll.gap-4.d-flex.align-center.px-4.pb-2(v-drag-scroll)
      CommentaryTypeChip(
        v-model:commentary-type-machine-name="commentaryTypeMachineName"
        :counts="commentaryTypeCounts"
      )
      CommentaryDetailChip(
        v-model:detail-level-weight="commentaryFiltersStore.detailLevelWeight"
        :counts="detailLevelCounts"
      )
      CommentaryLanguagesChip(
        v-model:languages="commentaryLanguages"
        :counts="languageCounts"
      )
  VRow(v-if="commentaryQueryError")
    VCol
      ErrorAlert(
        :reporting="{ message: commentaryQueryError.message, extra: commentaryQueryError }"
        :message="commentaryQueryError.message"
        :stack="commentaryQueryError.stack"
      )
  VRow(v-else-if="commentaryQuery.isPending.value")
    VCol
      VSheet.mb-4.mt-4.pa-4(:rounded="true")
        VSkeletonLoader(type="text@2")
  div.d-flex.flex-column.gap-4.pt-2(v-else)
    div.commentary-doc(
      v-for="doc in combinedCommentLists.fromRange"
      :key="doc.nid"
    )
      CommentTeaserCard(
        v-if="doc.comment"
        :heading="doc.comment.heading"
        :ranges="doc.comment.ranges"
        :show-ranges="false"
        :detail-level-name="doc.comment.detailLevel.name[locale] ?? '?'"
        :detail-level-num-dots="numDotsPerDetailLevelWeight[doc.comment.detailLevel.weight] ?? 0"
        :show-commentary-type="false"
        :commentary-type-machine-name="commentaryTypeMachineName"
        :nid="doc.nid"
        :local-tid="localTid"
        :show-default-reading-list="true"
        :show-reading-list-menu="true"
        :language="doc.comment.language"
      )
      CommentaryImage(
        v-else-if="doc.image"
        :title="doc.image.title"
        :description="doc.image.description"
        :large-url="doc.image.styles.large"
        :small-url="doc.image.styles.small"
        :placeholder-data="doc.image.styles.placeholder"
        :original-url="doc.image.styles.original"
      )
    div.hr(
      v-if="combinedCommentLists.overlapRange.length > 0 && combinedCommentLists.fromRange.length > 0"
    )
    div.commentary-doc(
      v-for="doc in combinedCommentLists.overlapRange"
      :key="doc.nid"
    )
      CommentTeaserCard(
        v-if="doc.comment"
        :heading="doc.comment.heading"
        :ranges="doc.comment.ranges"
        :show-ranges="true"
        :detail-level-name="doc.comment.detailLevel.name[locale] ?? '?'"
        :detail-level-num-dots="numDotsPerDetailLevelWeight[doc.comment.detailLevel.weight] ?? 0"
        :show-commentary-type="false"
        :commentary-type-machine-name="commentaryTypeMachineName"
        :nid="doc.nid"
        :local-tid="localTid"
        :show-default-reading-list="true"
        :show-reading-list-menu="true"
        :language="doc.comment.language"
      )
      CommentaryImage(
        v-else-if="doc.image"
        :title="doc.image.title"
        :description="doc.image.description"
        :large-url="doc.image.styles.large"
        :small-url="doc.image.styles.small"
        :placeholder-data="doc.image.styles.placeholder"
        :original-url="doc.image.styles.original"
      )
VDivider
div.d-flex.align-center.justify-space-between.pa-4.bg-background
  VBtn(
    color="secondary-c500"
    variant="flat"
    :to="localePath(getCommentaryLocationFromRange(commentaryTypeMachineName, range, undefined, locale))"
  )
    CommentaryTypeIcon.mr-2(
      :machine-name="commentaryTypeMachineName"
      variant="outline"
    )
    | {{ i18n.t("Global.study") }}
  VBtn(
    variant="outlined"
    @click="openSheet = false"
  )
    VIcon {{ mdiClose }}
    | {{ i18n.t("Global.close") }}
</template>

<script setup lang="ts">
import type { TranslationAgnosticVidRange } from "~/units/scripture";
import {
  makeRange,
  rangeToVidsString,
  renderRangeReference,
} from "~/units/scripture";
import { type Locale, useI18n } from "vue-i18n";
import { usePreferencesStore } from "~/stores/usePreferencesStore";
import { useLastCommentListStore } from "~/stores/useLastCommentListStore";
import type {
  ProcessedCommentaryComment,
  ProcessedCommentaryImage,
} from "@/composables/processCommentarySearchResults/types";
import { injectRequired } from "~/injectRequired";
import {
  InModalViewKey,
  LogFunctionKey,
  ScriptureClientKey,
  SolrClientKey,
} from "~/injectionKeys";
import { getCommentaryLocationFromRange } from "~/routing/locations";
import { getCommentaryTypeOrFallback } from "~/static/taxonomy/commentaryTypes";
import type { SerializableErrorInfo } from "~/errors/util";
import { localesByCode } from "~/i18n/locales";
import { never } from "~/never";
import { computeDocList } from "./CommentaryGroup.util";
import { numDotsPerDetailLevelWeight } from "~/static/taxonomy/detailLevels";
import { useTheme } from "vuetify";
import HH from "~/headings/components/HH.vue";
import { mdiClose } from "@mdi/js";

provide(InModalViewKey, true);

const solrClient = injectRequired(SolrClientKey);
const scriptureClient = injectRequired(ScriptureClientKey);
const log = injectRequired(LogFunctionKey);
const theme = useTheme();
const i18n = useI18n();
const preferencesStore = usePreferencesStore();
const localePath = useLocalePath();

const locale = i18n.locale;

interface Props {
  range: TranslationAgnosticVidRange;
  openSheet: boolean;
  defaultCommentaryTypeMachineName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  defaultCommentaryTypeMachineName: "exposition",
});

const emit = defineEmits<{
  "update:openSheet": [value: boolean];
}>();

const openSheet = computed<boolean>({
  get() {
    return props.openSheet;
  },
  set(value) {
    emit("update:openSheet", value);
  },
});

const localeConfig = computed(
  () => localesByCode.get(locale.value) ?? never("Locale not found."),
);
const localTid = computed<string>(() => localeConfig.value.defaultTid);
const commentaryFiltersStore = useCommentaryFiltersStore();

const commentaryTypeMachineName = ref<string>(
  getCommentaryTypeOrFallback(props.defaultCommentaryTypeMachineName)
    .machineName,
);

const commentaryLanguages = computed<Locale[]>({
  get: () => preferencesStore.acceptedLanguages,
  set(value) {
    preferencesStore.acceptedLanguages = value;
  },
});

const vidRange = computed(() =>
  makeRange(props.range.fromVid, props.range.toVid),
);
const humanReadableRange = useComputedResultAsync(() =>
  renderRangeReference(
    vidRange.value,
    scriptureClient,
    localTid.value,
    false,
    false,
  ),
);

const commentaryQuery = useCommentaryQuery(
  solrClient,
  log,
  computed(() => ({
    vids: rangeToVidsString(vidRange.value),
    detailLevelWeight: commentaryFiltersStore.detailLevelWeight ?? 4,
    commentaryTypeMachineName: commentaryTypeMachineName.value,
  })),
  computed(() => preferencesStore.getLanguages(unref(i18n.locale))),
  scriptureClient, // TEMPORARY SCRIP-40 Solr compatibility.
);

const commentaryQueryError = computed<Error | SerializableErrorInfo | null>(
  () =>
    commentaryQuery.error.value ?? commentaryQuery.data.value?.error ?? null,
);

const commentsList = computed<
  (ProcessedCommentaryComment | ProcessedCommentaryImage)[]
>(() => {
  return commentaryQuery.data.value?.value?.flatResults ?? [];
});

type FromAndOverlapRangeLists = {
  fromRange: (ProcessedCommentaryComment | ProcessedCommentaryImage)[];
  overlapRange: (ProcessedCommentaryComment | ProcessedCommentaryImage)[];
};

const combinedCommentLists = computed(() => {
  const flatResults: (ProcessedCommentaryComment | ProcessedCommentaryImage)[] =
    commentaryQuery.data.value?.value?.flatResults ?? [];

  const lists: FromAndOverlapRangeLists = flatResults.reduce(
    (
      acc: FromAndOverlapRangeLists,
      currentComment: ProcessedCommentaryComment | ProcessedCommentaryImage,
    ): FromAndOverlapRangeLists => {
      const range = currentComment?.ranges?.[0];
      if (
        (range == null ? undefined : rangeToVidsString(range)) ===
        rangeToVidsString(vidRange.value)
      ) {
        acc.fromRange.push(currentComment);
      } else {
        acc.overlapRange.push(currentComment);
      }
      return acc;
    },
    { fromRange: [], overlapRange: [] },
  );

  return {
    fromRange: computeDocList(lists.fromRange),
    overlapRange: computeDocList(lists.overlapRange),
    ordored: [...lists.fromRange, ...lists.overlapRange],
  };
});

const { setList, clear } = useLastCommentListStore();

watchEffect(() => {
  setList(combinedCommentLists.value.ordored);
});

onBeforeUnmount(() => {
  clear();
});

const title = computed<string>(() => {
  return (
    humanReadableRange.value ||
    i18n.t(
      "BibleVerseCommentList.VerseReferenceTitleFallback",
      commentsList.value.length,
    )
  );
});

const commentaryTypeCounts = computed<Map<string, Map<string, number>>>(
  () => commentaryQuery.data.value?.value?.commentaryTypeCounts ?? new Map(),
);

const detailLevelCounts = computed<Map<string, Map<number, number>>>(
  () => commentaryQuery.data.value?.value?.detailLevelCounts ?? new Map(),
);

const languageCounts = computed<Map<string, number>>(
  () => commentaryQuery.data.value?.value?.languageCounts ?? new Map(),
);
</script>
